import React from "react"
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from "@mui/styles";
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { FlexWrapper, Typography } from "../../StyledComponents";
import {
  AnalysisPanel,
  CategoryLabel,
  CategoryRow,
  DataCell,
  FieldPill,
  getPublicRecordRateTone,
  HeaderCell,
  PanelIntro,
  PercentCheck,
  PercentRing,
  PercentText,
  PercentValue,
  StatusIconCell,
  TableHeaderRow,
  TableRow,
  TableShell,
  TableViewport,
} from "./ConfigurationTableLayout";

const performanceColumns = "minmax(17rem, 1.5fr) minmax(6rem, 0.45fr) repeat(5, minmax(8.5rem, 0.8fr))"

const displayParameterLabel = (label) => (
  label.includes('Primary') ?
    label.replace('Primary', '').toUpperCase()
  : label.includes('Secondary') ?
    label.replace('Secondary', '').toUpperCase()
  :
    label.toUpperCase()
)

const getPopulatedPercent = (sweepParameter) => (
  Number((((sweepParameter.populated)/sweepParameter.totalQueries) * 100).toFixed(0))
)

const ParameterPerformanceStats = ({ mortgageDataModel }) => {
  const theme = useTheme()

  return (
    <AnalysisPanel>
      <PanelIntro>
        <span>All fields can be used to track changes to a property or its respective ownership status.</span>
        <span>When "Active", a discrepancy found in a given field will trigger an Investigation.</span>
        <span>The table below shows how each one is performing in monitoring your data.</span>
      </PanelIntro>
      <TableShell>
        <TableViewport>
          <TableHeaderRow style={{gridTemplateColumns: performanceColumns}}>
            <HeaderCell>label</HeaderCell>
            <HeaderCell $center>
              active
              <Tooltip
                componentsProps={{tooltip: {sx: {
                  color: `${theme.tooltip.info.color}`,
                  backgroundColor: `${theme.tooltip.info.background}`,
                  border: `0.3rem solid ${theme.tooltip.info.border}`,
                  borderBottom: `0.2rem solid ${theme.tooltip.info.border}`,
                  opacity: '0',
                  width: '40rem',
                  minWidth: '40rem',
                  padding: '3rem 0',
                }}}}
                PopperProps={{modifiers: [{
                  name: "offset",
                  options: {
                    offset: [0, 0],
                  },
                }]}}
                title={
                  <FlexWrapper className="column">
                    <Typography className="justifyCenter alignCenter">
                      When "Active", discrepancies found in this field will trigger an investigation.
                    </Typography>
                    <Typography className="justifyCenter alignCenter" style={{marginTop: '0.3rem'}}>
                      Statistics are only recorded on active fields.
                    </Typography>
                  </FlexWrapper>
                } 
                placement="top"
                arrow
              >
                <InfoIcon />
              </Tooltip>
            </HeaderCell>
            <HeaderCell>How often: <br/> the field is found in Public Records</HeaderCell>
            <HeaderCell $tone="discrepancy">When Found - how often: <br/> Discrepancies are present</HeaderCell>
            <HeaderCell $tone="renegotiation">When a Discrepancy - how often: <br/> a Renegotiation results</HeaderCell>
            <HeaderCell $tone="refinance">When a Discrepancy - how often: <br/> a Refinance results</HeaderCell>
            <HeaderCell $tone="dismissal">When a Discrepancy - how often: <br/> a Dismissal results</HeaderCell>
          </TableHeaderRow>
          {mortgageDataModel.map((category, index) => (index !== 0 &&
            <div key={category.subCat}>
              <CategoryRow style={{gridTemplateColumns: performanceColumns}}>
                <CategoryLabel>{category.subCat}</CategoryLabel>
              </CategoryRow>
              {category.fields.map((sweepParameter, index) => (
                <TableRow
                  key={sweepParameter.label}
                  $even={index%2 === 1}
                  style={{gridTemplateColumns: performanceColumns}}
                >
                  <DataCell>
                    <FieldPill $tier={sweepParameter.assignedTier}>
                      {displayParameterLabel(sweepParameter.label)}
                    </FieldPill>
                  </DataCell>
                  <StatusIconCell $center>
                    {sweepParameter.active ?
                      <RadioButtonCheckedIcon style={{color: `${theme.palette.primary.main}`}} />
                    :
                      <RadioButtonUncheckedIcon style={{color: `${theme.palette.common.grayBorderMedium}`}} />
                    }
                  </StatusIconCell>
                  <DataCell $center $strong={sweepParameter.populated > 0}>
                    {sweepParameter.populated > 0 ?
                      <PercentValue>
                        <PercentRing
                          $percent={getPopulatedPercent(sweepParameter)}
                          $tone={getPublicRecordRateTone(getPopulatedPercent(sweepParameter))}
                        >
                          {getPopulatedPercent(sweepParameter) === 100 &&
                            <PercentCheck $tone={getPublicRecordRateTone(getPopulatedPercent(sweepParameter))}>✓</PercentCheck>
                          }
                        </PercentRing>
                        <PercentText $tone={getPublicRecordRateTone(getPopulatedPercent(sweepParameter))}>
                          {((((sweepParameter.populated)/sweepParameter.totalQueries) * 100).toFixed(0) + '%')}
                        </PercentText>
                      </PercentValue>
                    :
                      '-'
                    }
                  </DataCell>
                  <DataCell $center $strong={sweepParameter.discrepancies > 0} $tone="discrepancy">
                    {sweepParameter.discrepancies > 0 ?
                      (((sweepParameter.discrepancies/(sweepParameter.populated)) * 100).toFixed(0) + '%')
                    :
                      '-'
                    }
                  </DataCell>
                  <DataCell $center $strong={sweepParameter.renegoitation > 0} $tone="renegotiation">
                    {sweepParameter.renegoitation > 0 ?
                      (((sweepParameter.renegoitation/sweepParameter.discrepancies) * 100).toFixed(0) + '%')
                    :
                      '-'
                    }
                  </DataCell>
                  <DataCell $center $strong={sweepParameter.refinances > 0} $tone="refinance">
                    {sweepParameter.refinances > 0 ?
                      (((sweepParameter.refinances/sweepParameter.discrepancies) * 100).toFixed(0) + '%')
                    :
                      '-'
                    }
                  </DataCell>
                  <DataCell $center $strong={sweepParameter.dismissals > 0} $tone="dismissal">
                    {sweepParameter.dismissals > 0 ?
                      (((sweepParameter.dismissals/sweepParameter.discrepancies) * 100).toFixed(0) + '%')
                    :
                      '-'
                    }
                  </DataCell>
                </TableRow>
              ))}
            </div>
          ))}
        </TableViewport>
      </TableShell>
    </AnalysisPanel>
  )
}

export default ParameterPerformanceStats;
